import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, CheckCircle, Users, Wrench, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    project_type: "",
    property_location: "",
    estimated_budget: "",
    project_description: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success("Enquiry submitted successfully! We'll be in touch within 24-48 hours.");
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        project_type: "",
        property_location: "",
        estimated_budget: "",
        project_description: ""
      });
    } catch (error) {
      toast.error("Failed to submit enquiry. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const trustPoints = [
    { icon: Users, text: "2–4 Person Experienced Team" },
    { icon: CheckCircle, text: "Fully Managed Projects" },
    { icon: Wrench, text: "Professional & Clean Workmanship" },
    { icon: Sparkles, text: "High-End Finishing Standards" }
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center bg-gradient-to-b from-charcoal to-stone-700" data-testid="contact-hero">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 text-center w-full">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-stone-50 mb-4"
          >
            Start Your Project
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-body text-base md:text-lg text-stone-300 max-w-2xl mx-auto"
          >
            Tell us about your space and we'll be in touch to discuss the next steps.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-stone-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="font-body text-sm text-charcoal">
                      Full Name
                    </Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      data-testid="input-full-name"
                      className="bg-white border-stone-200 focus:border-bronze focus:ring-bronze"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-body text-sm text-charcoal">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      data-testid="input-email"
                      className="bg-white border-stone-200 focus:border-bronze focus:ring-bronze"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-body text-sm text-charcoal">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+44 7XXX XXXXXX"
                      data-testid="input-phone"
                      className="bg-white border-stone-200 focus:border-bronze focus:ring-bronze"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="project_type" className="font-body text-sm text-charcoal">
                      Project Type
                    </Label>
                    <Select
                      value={formData.project_type}
                      onValueChange={(value) => handleSelectChange("project_type", value)}
                    >
                      <SelectTrigger data-testid="select-project-type" className="bg-white border-stone-200">
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Luxury Bathroom">Luxury Bathroom</SelectItem>
                        <SelectItem value="Refurbishment">Refurbishment</SelectItem>
                        <SelectItem value="Unsure / Need Advice">Unsure / Need Advice</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="property_location" className="font-body text-sm text-charcoal">
                      Property Location
                    </Label>
                    <Input
                      id="property_location"
                      name="property_location"
                      value={formData.property_location}
                      onChange={handleChange}
                      required
                      placeholder="Town or postcode"
                      data-testid="input-location"
                      className="bg-white border-stone-200 focus:border-bronze focus:ring-bronze"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="estimated_budget" className="font-body text-sm text-charcoal">
                      Estimated Budget
                    </Label>
                    <Select
                      value={formData.estimated_budget}
                      onValueChange={(value) => handleSelectChange("estimated_budget", value)}
                    >
                      <SelectTrigger data-testid="select-budget" className="bg-white border-stone-200">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Under £5,000">Under £5,000</SelectItem>
                        <SelectItem value="£5,000–£10,000">£5,000–£10,000</SelectItem>
                        <SelectItem value="£10,000–£20,000">£10,000–£20,000</SelectItem>
                        <SelectItem value="£20,000+">£20,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project_description" className="font-body text-sm text-charcoal">
                    Project Description
                  </Label>
                  <Textarea
                    id="project_description"
                    name="project_description"
                    value={formData.project_description}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project..."
                    rows={5}
                    data-testid="input-description"
                    className="bg-white border-stone-200 focus:border-bronze focus:ring-bronze resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="submit-enquiry-btn"
                  className="btn-primary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                </button>

                <p className="font-body text-xs text-cool-grey">
                  We aim to respond within 24–48 hours.
                </p>
              </form>
            </motion.div>

            {/* Contact Details Panel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="bg-stone-100 rounded-sm p-8" data-testid="contact-details-panel">
                <h3 className="font-heading text-xl text-charcoal mb-6">
                  Direct Contact
                </h3>
                
                <div className="space-y-4 mb-8">
                  <a
                    href="tel:+4407985483822"
                    className="flex items-center gap-3 font-body text-sm text-charcoal hover:text-bronze transition-colors duration-300"
                    data-testid="contact-phone"
                  >
                    <Phone size={18} strokeWidth={1.5} className="text-bronze" />
                    +44 07985 483822
                  </a>
                  <a
                    href="mailto:info@PJMWServices.co.uk"
                    className="flex items-center gap-3 font-body text-sm text-charcoal hover:text-bronze transition-colors duration-300"
                    data-testid="contact-email"
                  >
                    <Mail size={18} strokeWidth={1.5} className="text-bronze" />
                    info@PJMWServices.co.uk
                  </a>
                </div>

                <div className="border-t border-stone-200 pt-8 mb-8">
                  <h4 className="font-body text-xs uppercase tracking-widest text-cool-grey mb-4">
                    Service Area
                  </h4>
                  <p className="font-body text-sm text-charcoal flex items-start gap-3 mb-5">
                    <MapPin size={18} strokeWidth={1.5} className="text-bronze flex-shrink-0 mt-0.5" />
                    <span>
                      Based in Northamptonshire. We primarily operate across Northamptonshire and surrounding counties for larger projects.
                    </span>
                  </p>
                  <div className="relative rounded-sm overflow-hidden" data-testid="service-area-map">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d311747.4044051498!2d-0.9!3d52.27!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2suk!4v1700000000000!5m2!1sen!2suk"
                      width="100%"
                      height="220"
                      style={{ border: 0, filter: "grayscale(0.3) contrast(1.05)" }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="PJMW Services - Northamptonshire Service Area"
                    />
                    {/* Service radius overlay */}
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                      <div className="w-36 h-36 rounded-full border-2 border-bronze/50 bg-bronze/10 animate-pulse" />
                    </div>
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                      <div className="w-24 h-24 rounded-full border border-bronze/30 bg-bronze/5" />
                    </div>
                    <div className="absolute bottom-2 left-2 bg-charcoal/80 px-2.5 py-1 rounded-sm">
                      <span className="font-body text-[10px] text-stone-200 uppercase tracking-wider">Northamptonshire & Surrounding Areas</span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-stone-200 pt-8">
                  <h4 className="font-body text-xs uppercase tracking-widest text-cool-grey mb-4">
                    Why Choose Us
                  </h4>
                  <div className="space-y-3">
                    {trustPoints.map((point) => (
                      <div
                        key={point.text}
                        className="flex items-center gap-3"
                        data-testid={`trust-point-${point.text.slice(0, 10).replace(/\s/g, '-')}`}
                      >
                        <point.icon size={16} strokeWidth={1.5} className="text-bronze" />
                        <p className="font-body text-sm text-charcoal">
                          {point.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
